import { AI_APP_IDS } from "../Trainer/AppWorkouts/apps";
import FilterState, {
    type Filter,
} from "../Trainer/AppWorkouts/apps/shared/filterState";
import { Sport } from "../common/context/sport";

export interface HomeScreenItem {
    name: string;
    label: string;
    subtitle: string;
    iconColor: string;
    navUrl: string;
    order: number;
    sports?: Sport[];
    featureFlag?: string;
    /** Optional pill rendered at the trailing edge of the row (e.g. "New"). */
    badge?: string;
    setFilter?: () => void;
}

const defaultFilters: Filter = {
    appId: null,
    position: false,
    providerIds: [],
    excludeProviderIds: [],
    tagIds: [],
    contentMode: "volleyOnly",
};

/**
 * Feature that surfaces the padel assessment entry and its dashboard.
 *
 * Not an access check — the start endpoint gates nothing on it, and a player who
 * knows the URL can take the assessment without it. It says whether this
 * environment has padel journeys and workouts authored, which is the one thing
 * the client cannot work out for itself. Grant it once content lands.
 */
export const PADEL_ASSESSMENT_FEATURE = "PADEL_ASSESSMENT";

/**
 * Cohort switch for the Club-in-Club home widget.
 *
 * Not an access check — the server endpoint is gated on its own `CLUB_IN_CLUB_ENABLED`
 * config, not on this, so an admin can exercise it before anyone is granted the feature.
 * This says "show this person the widget", and should be granted to a segment scoped to
 * the pilot club: everyone else would only ever see it render nothing.
 */
export const CLUB_IN_CLUB_FEATURE = "CLUB_IN_CLUB";

export const mainWorkoutItems: HomeScreenItem[] = [
    {
        // Base copy is the not-linked (3a) variant; NewHomeScreenLibrary
        // overrides the title for linked-but-unrated users and drops this item
        // entirely for users who have already submitted a rating. Availability
        // is the sport alone — no feature flag.
        name: "long_form_assessment",
        label: "Don't have a DUPR? Get Rated",
        subtitle: "Take the 10-minute assessment",
        iconColor: "#3CE97C",
        navUrl: "/journeys/long-form",
        order: 0,
        sports: ["PICKLEBALL"],
        badge: "New",
    },
    {
        name: "performance_dashboard",
        label: "Know Your Game",
        subtitle: "Get an objective skills rating. Level up with Volley",
        iconColor: "#3b82f6",
        navUrl: "/performance",
        order: 0,
        sports: ["PICKLEBALL"],
    },
    {
        // Unlike its DUPR twin above, this is gated on a feature as well as the
        // sport. The DUPR assessment is content that exists in every
        // environment; the padel journeys and workouts are authored per
        // environment, and a player who taps this before they land gets a 404
        // from the start endpoint. The flag is what says "padel content is live
        // here" — nothing else can, since the client can't see the journeys.
        name: "padel_assessment",
        label: "Know Your Padel Game",
        subtitle: "Take the 10-minute assessment",
        iconColor: "#3CE97C",
        navUrl: "/journeys/padel-assessment",
        featureFlag: PADEL_ASSESSMENT_FEATURE,
        order: 0,
        sports: ["PADEL"],
        badge: "New",
    },
    {
        name: "performance_dashboard",
        label: "Know Your Game",
        subtitle: "Level up with Volley",
        iconColor: "#3b82f6",
        navUrl: "/journeys/assessments",
        featureFlag: "ASSESSMENT_DASHBOARD_V2",
        order: 0,
        sports: ["PLATFORM_TENNIS", "TENNIS", "PADEL"],
    },
    {
        name: "quick_start",
        label: "Quick Start",
        subtitle: "Choose what to hit then press play",
        iconColor: "#4ade80",
        navUrl: "/content/apps/workouts/experimental/basic/play?tag=BASIC_FRONTCOURT_SIDE",
        order: 1,
    },
    {
        name: "ai_workouts",
        label: "Interactive Workouts",
        subtitle: "Powered by Volley's camera system",
        iconColor: "#a855f7",
        navUrl: "/content/apps/workouts/search",
        setFilter: () => {
            FilterState.setFilter("search", {
                ...defaultFilters,
                appId: AI_APP_IDS,
            });
        },
        order: 3,
    },
    {
        name: "multi_level",
        label: "Multi Level",
        subtitle: "Train an area of your game at a chosen difficulty",
        iconColor: "#56D5EF",
        navUrl: "/content/apps/workouts/search",
        setFilter: () => {
            FilterState.setFilter("search", {
                ...defaultFilters,
                appId: [9],
            });
        },
        order: 4,
    },
    {
        name: "skill_building",
        label: "Skill Building",
        subtitle: "Sequenced workouts paired with pro instruction",
        iconColor: "#DA30D8",
        navUrl: "/content/apps/workouts/search",
        setFilter: () => {
            FilterState.setFilter("search", {
                ...defaultFilters,
                appId: [11],
            });
        },
        order: 5,
    },
    {
        name: "single_shots",
        label: "Single Shots",
        subtitle: "Customize a specific shot for targeted reps",
        iconColor: "#EFA511",
        navUrl: "/content/apps/workouts/search",
        setFilter: () => {
            FilterState.setFilter("search", {
                ...defaultFilters,
                appId: [5],
            });
        },
        order: 6,
    },
    {
        name: "classes_at_location",
        label: "Classes",
        subtitle: "Guided classes from your home club",
        iconColor: "#3CE97C",
        navUrl: "/content/classes",
        featureFlag: "HOME_SCREEN_CLASSES",
        order: 7,
    },
    {
        name: "all_workouts",
        label: "All Workouts",
        subtitle: "Explore the complete workout library",
        iconColor: "#ffffff",
        navUrl: "/content/apps/workouts/search",
        setFilter: () => {
            FilterState.setFilter("search", { ...defaultFilters });
        },
        order: 8,
    },
];

export interface PersonalItem {
    name: string;
    label: string;
    subtitle?: string;
    navUrl: string;
    sportFilter?: string[];
    setFilter?: () => void;
}

export const personalItems: PersonalItem[] = [
    {
        name: "my_workouts",
        label: "My Workouts (Create & Play)",
        navUrl: "/content/apps/workouts/user",
        setFilter: () => {
            FilterState.setFilter("user", {
                ...defaultFilters,
                contentMode: "mixed",
            });
        },
    },
    {
        name: "favorites",
        label: "Favorites",
        navUrl: "/content/apps/workouts/favorites",
        setFilter: () => {
            FilterState.setFilter("favorites", {
                ...defaultFilters,
                contentMode: "mixed",
            });
        },
    },
    {
        name: "recently_played",
        label: "Recently Played",
        navUrl: "/content/apps/workouts/recent",
    },
    {
        name: "pti_stats",
        label: "PTI Stats",
        navUrl: "/player-pti",
        sportFilter: ["PLATFORM_TENNIS"],
    },
];
